/**
 * Losing the single session is an EXPECTED outcome, and every product should react the
 * same way: hand over to the login app (`${secureUrl}/logout`) and let it own the
 * sign-out flow and the copy. mhub-post-sales-web already does exactly that in its
 * `signOut`; this module is the CRM's half of the same contract, kept small and free of
 * app imports so another product can lift it as-is.
 *
 * Background: `mhub-user-api POST /usersession/create` writes the one row that represents
 * "the" session for a user. Since mhub-user-api#137 that write is monotonic in
 * `auth_time`, so a session which a NEWER sign-in already replaced is refused with 403
 * instead of being handed the slot back. Before that guard a superseded tab reclaimed the
 * session simply by reloading, and the two sessions ping-ponged (GMD-1031).
 */

/**
 * isSupersededSession reports whether an error from /usersession/create or /usersession/ping
 * means "a newer sign-in owns this session", as opposed to a transient failure worth retrying.
 *
 * Matched on the MESSAGE only — see the comment inside; a status-first version of this
 * predicate classified every gateway/WAF 403 as a takeover.
 *
 * @param {unknown} error an error thrown by the Amplify API call
 * @returns {boolean} true when the session was superseded, so sign out rather than retry
 */
export const isSupersededSession = (error) => {
  // The MESSAGE decides, never the status alone. mhub-user-api's supersede answer is a 403
  // whose error_message says "a newer session is active for this user", and that message
  // travels with every shape the error can arrive in. A bare status check classified EVERY
  // 403 on this route as a takeover — including a gateway/WAF 403 carrying no such message —
  // which signed the user out with a fabricated "someone signed in elsewhere" explanation
  // and suppressed the Bugsnag report that would have surfaced the real fault. A supersede
  // we fail to recognise degrades to the generic error path (retries, then a bare /logout):
  // worse copy, but never a lie.
  const message = error?.response?.data?.error_message
    || error?.response?.data?.message
    || error?.message
    || '';
  return /newer session is active/i.test(String(message));
};

export default isSupersededSession;

/**
 * The CRM services answer `invalid user session` when the session row no longer matches the
 * caller's token — i.e. someone else signed in and took the single session. It arrives as an
 * ordinary GraphQL error, so without this it lands in each caller's generic handler: a
 * snackbar saying "An error has occured. [sic] Please try again." and, because enhancedQuery falls
 * back to defaultData, a detail page that renders with no data. Neither says the true thing,
 * and retrying can never fix it.
 *
 * @param {ReadonlyArray<{message?: string}>} [graphQLErrors]
 * @returns {boolean} true when the caller's session has been taken over
 */
export const isInvalidUserSession = (graphQLErrors) => (
  Array.isArray(graphQLErrors)
  && graphQLErrors.some((e) => /invalid user session/i.test(String(e?.message || '')))
);

let sessionLostHandled = false;

/**
 * Hand a lost session to the login app — the same contract every other product follows, and
 * the same thing this app already does when the Cognito token itself is missing.
 *
 * The reason travels with it so the login app can say WHY, instead of showing a bare "Signed
 * out" screen to someone who did not choose it. The login app allowlists the value, so an
 * unrecognised one degrades to no message rather than being rendered.
 *
 * It must be `/logout`, not `/login`: the id-token is still VALID (only the session row is
 * gone), so a login screen would see an authenticated session and send the user straight back,
 * where the next query fails again — a loop. Signing out is what breaks it.
 *
 * One-shot, because a page loads many queries at once and every one of them fails together;
 * without the latch each would start its own navigation.
 *
 * Takes a navigate function rather than a location object so nothing assigns to a caller's
 * parameter, and so a test can capture the destination without touching window.
 *
 * @param {string} secureUrl base URL of the login app
 * @param {(url: string) => void} [navigate] performs the navigation
 * @returns {boolean} whether this call performed the hand-off
 */
export const handleSessionLost = (secureUrl, navigate) => {
  if (sessionLostHandled) return false;
  const go = navigate || ((url) => { window.location.href = url; });
  // The latch flips only AFTER the navigation call returns: flipping it first meant a
  // navigation that threw left the latch stuck ON, and every later — genuine — takeover
  // call returned false to call sites that ignore the return value. The user then stayed
  // on a dead session with no redirect at all. Concurrent callbacks are still collapsed:
  // the assignment below lands before the next callback runs (callbacks are sequential
  // on one thread), so only the first successful call navigates.
  try {
    go(`${secureUrl}/logout?reason=session-taken-over`);
  } catch (navigationError) {
    // eslint-disable-next-line no-console
    console.error(
      'session hand-off navigation failed; leaving the latch open for a retry:',
      navigationError,
    );
    return false;
  }
  sessionLostHandled = true;
  return true;
};

/** Test-only: reset the one-shot latch. */
export const resetSessionLostLatch = () => {
  sessionLostHandled = false;
};
